import { createFileRoute, getRouteApi } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { SeriesHeader, TabHeading } from '~/components/ui/ContentNav';
import { createTablePage } from '~/components/ui/createTablePage';
import { LIST_PAGE_SIZE, Pagination } from '~/components/ui/Pagination';
import { Table } from '~/components/ui/Table';
import { formatWhen, VersionPill } from '~/components/ui/Versions';
import { CreateCourseButton } from './CreateCourseButton';

type CourseAdminSummary = components['schemas']['CourseAdminSummary'];

// Optional key, not an undefined value: a required key would force every
// `<Link to="/courses">` in the console to pass `search`.
type CoursesSearch = { series?: number };

// The route names this component and the component reads the route's search, so
// the two infer circularly. getRouteApi plus an explicit CoursesSearch breaks it.
const routeApi = getRouteApi('/_admin/courses/');

const columnHelper = createColumnHelper<CourseAdminSummary>();

// One row per course: its live version (or its only version), and a draft only when
// one is open, so "is anyone working on this?" shows at a glance.
const columns = [
  columnHelper.accessor('id', { header: 'ID' }),
  columnHelper.accessor('title', { header: 'Title' }),
  columnHelper.accessor('language', {
    header: 'Language',
    // Null for migrated courses that predate series assignment.
    cell: (c) => c.getValue() ?? '—',
  }),
  columnHelper.display({
    id: 'version',
    header: 'Version',
    cell: (c) => {
      const row = c.row.original;
      // A never-published course is its own draft.
      if (row.live_version == null)
        return (
          <>
            <VersionPill state="draft" version={row.version} />{' '}
            <Muted>not yet published</Muted>
          </>
        );
      return (
        <>
          <VersionPill state="live" version={row.live_version} />
          {row.draft_version != null && (
            <>
              {' '}
              <VersionPill
                state="draft"
                version={row.draft_version}
                detail={
                  row.draft_started_at
                    ? `Started${row.draft_started_by?.name ? ` by ${row.draft_started_by.name}` : ''} ${formatWhen(row.draft_started_at)}`
                    : undefined
                }
              />
            </>
          )}
        </>
      );
    },
  }),
  columnHelper.accessor('in_progress', { header: 'In progress' }),
];

const AllCourses = createTablePage<
  CourseAdminSummary,
  '/courses/$courseId/$version'
>({
  name: 'Courses',
  // Admin endpoint: all statuses (the student list_courses is gated to a fully
  // published path).
  useData: (page: number) =>
    api.adminListCourses.useQuery({
      query: { limit: LIST_PAGE_SIZE, offset: LIST_PAGE_SIZE * page },
    }),
  columns,
  itemBaseUrl: '/courses/$courseId/$version',
  getParams: (row) => ({
    courseId: String(row.id),
    version: String(row.version),
  }),
  actions: <CreateCourseButton />,
});

// `/courses?series=N`: the series' Courses tab, under the series' own header.
function SeriesCourses({ series }: { series: number }) {
  const [page, setPage] = useState(0);
  const seriesQuery = api.adminGetSeries.useQuery({
    path: { series_id: series },
  });
  const { data, isLoading, error } = api.adminListCourses.useQuery({
    query: {
      series_id: series,
      limit: LIST_PAGE_SIZE,
      offset: LIST_PAGE_SIZE * page,
    },
  });
  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const pagination = (hideWhenAlone = false) => (
    <Pagination
      page={page}
      pageSize={LIST_PAGE_SIZE}
      shown={data?.data.length ?? 0}
      total={data?.count}
      onPage={setPage}
      hideWhenAlone={hideWhenAlone}
    />
  );

  return (
    <div>
      {seriesQuery.data && <SeriesHeader series={seriesQuery.data} />}
      <TabHeading action={<CreateCourseButton />}>Courses</TabHeading>
      {isLoading ? (
        <p>Loading courses...</p>
      ) : error ? (
        <p>Error loading courses</p>
      ) : (
        <>
          {pagination()}
          <Table
            table={table}
            to="/courses/$courseId/$version"
            getParams={(row) => ({
              courseId: String(row.id),
              version: String(row.version),
            })}
          />
          {pagination(true)}
        </>
      )}
    </div>
  );
}

function CoursesPage() {
  const { series } = routeApi.useSearch() as CoursesSearch;
  return series !== undefined ? (
    <SeriesCourses series={series} />
  ) : (
    <AllCourses />
  );
}

export const Route = createFileRoute('/_admin/courses/')({
  validateSearch: (s: Record<string, unknown>): CoursesSearch => {
    const series = Number(s.series);
    return Number.isFinite(series) && series > 0 ? { series } : {};
  },
  component: CoursesPage,
});

const Muted = styled.span`
  color: var(--grey-700);
  font-size: 0.8125rem;
`;
